import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _isArray from 'lodash/isArray';
import _isEqual from 'lodash/isEqual';
import _split from 'lodash/split';
import { Prompt } from 'react-router';
import { Formik, Form } from 'formik';
import * as Sift from '@sift/types';
import * as Types from './typings';
import { LayoutUpdateShape } from 'pages/Features/typings';
import * as Entity from 'pages/Pages/typings';
import { Layout } from 'components/GenericLayoutBuilder/GenericLayoutBuilder';
import convertStringToArrayOfStrings from 'helpers/convertStringToArrayOfStrings';
import removeEmptyObjProps from 'helpers/removeEmptyObjProps';
import useEntityTypes, { PERSON_ENTITY_TYPE_KEY } from 'helpers/useEntityTypes';

// Components
import Page from 'components/Page/newComponents';
import PageFormToolbar from 'components/Page/newComponents/PageFormToolbar';
import PageFormErrorMessage from 'components/Page/newComponents/PageFormErrorMessage';
import SaveButton from 'components/Page/SaveButton';
import CancelButton from 'components/Page/CancelButton';
import AttributeBasicInfo from './components/AttributeBasicInfo';
import AttributeUsage from './components/AttributeUsage';
import AttributeReachOutToMeFor from './components/AttributeReachOutToMeFor';
import AttributeDataMapping from './components/AttributeDataMapping';
import AttributeUserEditing from './components/AttributeUserEditing';
import HandleRedirect from 'molecules/HandleRedirect';
import DeleteAttribute from 'pages/AttributesEdit/components/DeleteAttribute';
import Typography from '@material-ui/core/Typography';
import { attributeTypes } from 'pages/AttributesNew/AttributeTypeCard';

// Style
import makeStyles from '@material-ui/core/styles/makeStyles';

export interface LayoutObject {
  entityField: string;
  layoutKey: string;
  children: Layout;
}

interface AutocompleteData {
  title: string;
  type: string;
  subtitle: string | null;
  targetId: string | null;
}

interface Props {
  attribute: Sift.Field;
  entityType: Entity.EntityType;
  userFields: Sift.Field[];
  query: any;
  siftBotFields: Sift.Field[];
  layoutStatus: any;
  attributeAutocompleteData: AutocompleteData[] | null;
  updateField: (id: string, attribute: Types.PayloadField) => void;
  updateLayout: (payload: LayoutObject[]) => void;
  updateSiftBotFields: (payload: Sift.Field[]) => void;
  updateAttributeAutocomplete: (id: string, payload: { items: string[] }) => void;
}

function AttributesEdit({
  attribute,
  entityType,
  userFields,
  query,
  siftBotFields,
  layoutStatus,
  attributeAutocompleteData,
  updateField,
  updateLayout,
  updateSiftBotFields,
  updateAttributeAutocomplete,
}: Props) {
  const classes = useStyles();
  const { entityTypeKey } = useParams<{ entityTypeKey: string }>();
  const entityTypes = useEntityTypes();
  const personEntityType = _get(entityTypes, PERSON_ENTITY_TYPE_KEY);
  const [isSaving, setIsSaving] = useState(false);
  const attributeId = _get(attribute, 'id');

  useEffect(() => {
    document.title = 'Edit Attribute';
  }, []);
  const [formattedAutocompleteData, setFormattedAutocompleteData] = useState<null | string>(null);

  useEffect(() => {
    if (attributeAutocompleteData) {
      if (attributeAutocompleteData.length > 0) {
        setFormattedAutocompleteData(
          attributeAutocompleteData.map((item: AutocompleteData) => item.title).join(', '),
        );
      } else {
        setFormattedAutocompleteData('');
      }
    }
  }, [attributeAutocompleteData]);

  const item =
    _get(attributeTypes, `${attribute.baseField}`) ||
    _get(attributeTypes, `${attribute.objectKey}`);
  const shouldRenderDeleteAttribute = !query.newAttribute;
  const isSubAttribute = attribute.objectKey.includes('reachOutToMeFor.');
  const shouldRenderAttributeUsageAndDataMapping =
    _get(attribute, 'entityTypeId') === _get(personEntityType, 'id');

  const subAttribute = isSubAttribute ? (
    <div className={classes.subAttribute}>
      <Typography variant="caption">
        This is a sub-attribute of "Reach out to me for" attribute
      </Typography>
    </div>
  ) : null;

  const initialState = {
    client: {
      ...attribute,
    },
    layouts: {
      'org-chart-highlight-by': _get(personEntityType, '_layouts.org-chart-highlight-by', []),
      filters: _get(personEntityType, '_layouts.filters', []),
    },
    priorityList: siftBotFields || [],
    ...(attributeAutocompleteData && {
      autocomplete: formattedAutocompleteData,
    }),
  } as Types.AttributesEditFormValues;

  const initialStateToCompareDirty = useMemo(() => removeEmptyObjProps(initialState), [
    initialState,
  ]);
  const initialStateLayout = useMemo(() => _get(initialState, 'layouts'), [initialState]);

  const handleValidation = (values: Types.AttributesEditFormValues) => {
    const errors = {} as Types.AttributesFormErrors;

    if (values.client.displayName === '') {
      errors.displayName = 'Name is a required field.';
    }
    if (_get(attribute, 'baseField') === 'textSelect') {
      const selectOptions = _get(values, 'client.validation._selectOptions', null);
      if (!selectOptions || _isEmpty(selectOptions)) {
        errors.selectOptions = 'This attribute must have at least 1 option.';
      }
    }
    if (_get(attribute, 'baseField') === 'multiSelect') {
      const selectOptions = _get(values, 'client.item.validation._selectOptions', null);
      if (!selectOptions || _isEmpty(selectOptions)) {
        errors.selectOptions = 'This attribute must have at least 1 option.';
      }
    }
    return errors;
  };

  const handleUpdatePriorityFields = useCallback(
    (priorityFieldsPayload: Sift.Field[] | undefined) => {
      const isNewSiftFields = !_isEqual(priorityFieldsPayload, siftBotFields);
      if (priorityFieldsPayload && isNewSiftFields) {
        updateSiftBotFields(priorityFieldsPayload);
      }
    },
    [siftBotFields],
  );

  const handleUpdateLayout = useCallback(
    (layoutValues: LayoutUpdateShape | null) => {
      const layoutPayloadKeys = layoutValues
        ? Object.keys(layoutValues).filter(
            key => !_isEqual(layoutValues[key], _get(initialStateLayout, key)),
          )
        : null;

      const layoutPayload = layoutPayloadKeys
        ? layoutPayloadKeys.map(key => {
            return {
              entityField: PERSON_ENTITY_TYPE_KEY,
              layoutKey: key,
              children: _get(layoutValues, key, []),
            };
          })
        : null;

      if (layoutPayload && layoutPayload.length > 0) {
        updateLayout(layoutPayload);
      }
    },
    [initialStateLayout],
  );

  const handleUpdateField = useCallback(
    (clientPayload: Types.PayloadField) => {
      if (!_isEqual(clientPayload, _get(initialState, 'client'))) {
        attributeId && updateField(attributeId, clientPayload);
      }
    },
    [attributeId, initialState],
  );

  const handleUpdateAutocomplete = useCallback(
    (autocompletePayload: string | undefined) => {
      if (
        (autocompletePayload || formattedAutocompleteData) &&
        !_isEqual(autocompletePayload, _get(initialState, 'autocomplete', ''))
      ) {
        const arrayOfOptions = convertStringToArrayOfStrings(autocompletePayload);
        attributeId && updateAttributeAutocomplete(attributeId, { items: arrayOfOptions || [] });
      }
    },
    [formattedAutocompleteData, attributeId],
  );

  const handleSubmit = useCallback(
    (values: Types.AttributesEditFormValues) => {
      setIsSaving(true);

      //conditional updates
      const priorityFieldsPayload = values.priorityList;
      handleUpdatePriorityFields(priorityFieldsPayload);

      const layoutValues = _get(values, 'layouts', null);
      handleUpdateLayout(layoutValues);

      const clientPayload: Types.PayloadField = {
        ...values.client,
      };
      handleUpdateField(clientPayload);

      const autocompletePayload = _get(values, 'autocomplete');
      handleUpdateAutocomplete(autocompletePayload);
    },
    [initialState],
  );

  return (
    <Page classes={{ root: classes.pageRoot, content: classes.pageContent }}>
      {subAttribute}
      {attribute && entityType && (
        <Formik
          initialValues={initialState}
          enableReinitialize={true}
          onSubmit={handleSubmit}
          validate={handleValidation}
        >
          {props => {
            const { values, setFieldValue, resetForm } = props;
            const valuesToCompareDirty = removeEmptyObjProps(values);
            const errors = props.errors as Types.AttributesFormErrors;
            const dirty = !_isEqual(valuesToCompareDirty, initialStateToCompareDirty);
            const hasErrors = !_isEmpty(errors);

            return (
              <Form>
                <AttributeBasicInfo
                  attribute={attribute}
                  item={item || null}
                  values={values.client}
                  onChange={setFieldValue}
                  entityType={entityType}
                />
                {shouldRenderAttributeUsageAndDataMapping && (
                  <AttributeUsage
                    attribute={attribute}
                    fields={userFields}
                    values={values.layouts}
                    onChange={setFieldValue}
                    entityType={entityType}
                    status={layoutStatus}
                    typeKey={entityTypeKey}
                  />
                )}
                <AttributeReachOutToMeFor
                  attribute={attribute}
                  fields={userFields}
                  onChange={setFieldValue}
                />
                {shouldRenderAttributeUsageAndDataMapping && (
                  <AttributeDataMapping attribute={attribute} />
                )}
                <AttributeUserEditing
                  attribute={attribute}
                  entityType={entityType}
                  values={values}
                  onChange={setFieldValue}
                />
                {shouldRenderDeleteAttribute && (
                  <DeleteAttribute field={attribute} values={values} onChange={setFieldValue} />
                )}
                <PageFormToolbar
                  dirty={dirty || hasErrors}
                  preContent={<PageFormErrorMessage errors={errors} />}
                >
                  <CancelButton onClick={resetForm} disabled={hasErrors} />
                  <SaveButton
                    label="Save"
                    onClick={() => {
                      props.handleSubmit();
                      resetForm({ values });
                    }}
                    disabled={!dirty || hasErrors}
                  />
                </PageFormToolbar>
                <Prompt
                  when={isSaving ? false : dirty || hasErrors}
                  message={
                    'Your work is not saved, or you have errors that need to be addressed! Are you sure you want to leave?'
                  }
                />
                <HandleRedirect />
              </Form>
            );
          }}
        </Formik>
      )}
    </Page>
  );
}

const useStyles = makeStyles((theme: any) => ({
  root: {},
  pageRoot: {
    display: 'flex',
    flex: '1 1 auto',
    height: 'calc(100vh - 64px)',
  },
  pageContent: {
    alignItems: 'center',
  },
  title: {
    display: 'flex',
    flexDirection: 'row',
  },
  subAttribute: {
    background: 'rgba(41, 98, 255, 0.04) !important',
    border: `1px solid rgba(41, 98, 255, 0.12)`,
    borderRadius: theme.spacing(0.75),
    color: theme.palette.secondary.main,
    marginBottom: theme.spacing(3),
    padding: theme.spacing(0.5, 1),
    width: 'fit-content',
  },
}));

export default AttributesEdit;
